import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { ApiError } from '@orisini/ui/lib/api'
import { usePagination } from '@/components/pagination'
import { accountsApi } from '@/features/accounts/api'
import { pinsApi, type PinDto } from '../api'

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Waktu terbit dalam zona waktu AKUN pemilik pin, bukan zona browser: jadwal
 * disimpan epoch ms UTC dan slot dihitung di zona akun, jadi memakai zona
 * browser akan menampilkan jam yang berbeda dari yang dijadwalkan.
 */
function fmtTerbit(ms: number, tz: string): string {
  return new Date(ms).toLocaleString('en-GB', {
    timeZone: tz,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function PublishedPanel() {
  const [pins, setPins] = useState<PinDto[]>([])
  /** Zona waktu per akun — pin bisa milik beberapa akun dengan zona berbeda. */
  const [tzByAccount, setTzByAccount] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const { visible, pager } = usePagination(pins, 'pins')

  const load = useCallback(async () => {
    try {
      const [pinsRes, accountsRes] = await Promise.all([
        pinsApi.list({ status: 'posted', limit: 200 }),
        accountsApi.list(),
      ])
      // Server mengurutkan menurut waktu dibuat; yang relevan di sini kapan pin
      // itu benar-benar tayang.
      setPins([...pinsRes.pins].sort((a, b) => (b.postedAt ?? 0) - (a.postedAt ?? 0)))
      setTzByAccount(
        Object.fromEntries(accountsRes.accounts.map((a) => [a.id, a.timezone ?? BROWSER_TZ])),
      )
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t load published pins')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  function onDelete(pin: PinDto) {
    toast('Delete this pin from Pinboss?', {
      description: 'A pin already published on Pinterest is not deleted.',
      action: {
        label: 'Delete',
        onClick: () => {
          pinsApi
            .remove(pin.id)
            .then(() => {
              toast.success('Pin deleted')
              return load()
            })
            .catch((err: unknown) =>
              toast.error(err instanceof ApiError ? err.message : 'Couldn\'t delete'),
            )
        },
      },
    })
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>

  if (pins.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No published pins yet.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">{pins.length} published pins</p>

      <div className="space-y-2">
        {visible.map((pin) => (
          <div key={pin.id} className="flex items-center gap-3 rounded-lg border bg-card p-3">
            <div className="size-12 shrink-0 overflow-hidden rounded bg-muted">
              {pin.media?.previewUrl && pin.media.kind === 'image' ? (
                <img
                  src={pin.media.previewUrl}
                  alt=""
                  className="size-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex size-full items-center justify-center text-[10px] text-muted-foreground">
                  {pin.media?.kind ?? '—'}
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <Link
                to={`/pins/${pin.id}`}
                className="block truncate text-sm font-medium hover:underline"
              >
                {pin.title || pin.description || '(untitled)'}
              </Link>
              <p className="truncate text-xs text-muted-foreground">
                {pin.postedAt &&
                  `published ${fmtTerbit(pin.postedAt, tzByAccount[pin.accountId] ?? BROWSER_TZ)}`}
                {pin.board && <> · {pin.board.name}</>}
                {pin.link && <> · {pin.link}</>}
              </p>
            </div>

            {pin.pinterestPinUrl && (
              <a
                href={pin.pinterestPinUrl}
                target="_blank"
                rel="noreferrer"
                className="flex shrink-0 items-center gap-1 text-xs text-foreground hover:underline"
              >
                View on Pinterest <ExternalLink className="size-3" />
              </a>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
              title="Delete"
              onClick={() => onDelete(pin)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}
      </div>

      {pager}
    </div>
  )
}
